/* =========================================================
   Stanlee Reels — vanilla JS controller
   No dependencies. One instance per section on the page.
   ========================================================= */

(function () {
  'use strict';

  const DRAG_THRESHOLD = 70;

  class StanleeReels {
    constructor(root) {
      this.root = root;
      this.autoplay = root.dataset.autoplay === 'true';
      this.loop = root.dataset.loop === 'true';
      this.soundToggleEnabled = root.dataset.soundToggle === 'true';
      this.popupEnabled = root.dataset.popupEnabled !== 'false';

      this.trackWrapper = root.querySelector('[data-reels-track-wrapper]');
      this.track = root.querySelector('[data-reels-track]');
      this.cards = Array.from(root.querySelectorAll('[data-reel-card]'));
      this.prevBtn = root.querySelector('[data-reels-prev]');
      this.nextBtn = root.querySelector('[data-reels-next]');

      this.modal = root.querySelector('[data-reels-modal]');
      this.stage = root.querySelector('[data-reels-stage]');
      this.slides = Array.from(root.querySelectorAll('[data-reel-slide]'));
      this.dots = Array.from(root.querySelectorAll('[data-reel-dot]'));
      this.progressSegs = Array.from(root.querySelectorAll('[data-progress-seg]'));
      this.modalPrevBtn = root.querySelector('[data-reels-modal-prev]');
      this.modalNextBtn = root.querySelector('[data-reels-modal-next]');
      this.soundBtn = root.querySelector('[data-reels-sound]');
      this.likeBtn = root.querySelector('[data-reels-like]');
      this.shareBtn = root.querySelector('[data-reels-share]');
      this.closeEls = Array.from(root.querySelectorAll('[data-reels-close]'));

      this.activeIndex = 0;
      this.isMuted = true;
      this.touchStartY = null;
      this.wheelLocked = false;

      // Mouse-drag swipe state (desktop popup).
      this.isDragging = false;
      this.dragStartX = 0;
      this.dragDeltaX = 0;

      this.bindCardIntersection();
      this.bindCenteringObserver();
      this.bindTrackControls();
      this.bindCardClicks();
      this.bindAddToCartForms();

      if (this.modal) {
        this.bindModalControls();
        this.bindDragSwipe();
      }
    }

    /* ---------- Home grid: lazy load + autoplay on view ---------- */

    bindCardIntersection() {
      if (!('IntersectionObserver' in window)) {
        this.cards.forEach((card) => this.loadCardVideo(card));
        return;
      }

      this.cardObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const card = entry.target;
            const video = card.querySelector('[data-reel-video]');
            if (!video) return;

            if (entry.isIntersecting) {
              this.loadCardVideo(card);
              if (this.autoplay) {
                video.play().catch(() => {});
              }
            } else if (!entry.isIntersecting && !video.paused) {
              video.pause();
            }
          });
        },
        { root: null, rootMargin: '200px', threshold: 0.35 }
      );

      this.cards.forEach((card) => this.cardObserver.observe(card));
    }

    loadCardVideo(card) {
      const video = card.querySelector('[data-reel-video]');
      if (!video || video.dataset.loaded) return;
      const src = video.dataset.src;
      if (!src) return;
      video.src = src;
      video.loop = this.loop;
      video.dataset.loaded = 'true';
      video.load();
    }

    /* ---------- Requirement 1: auto-center when everything fits ---------- */

    bindCenteringObserver() {
      if (!this.trackWrapper || !this.track) return;

      const measure = () => {
        // A tiny tolerance avoids flapping right at the boundary.
        const fits = this.track.scrollWidth <= this.track.clientWidth + 2;
        this.trackWrapper.classList.toggle('is-fit', fits);
      };

      measure();

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => measure());
        this.resizeObserver.observe(this.track);
        this.resizeObserver.observe(this.trackWrapper);
      } else {
        window.addEventListener('resize', measure);
      }

      // Re-measure once videos/posters finish loading, since natural
      // image/video dimensions can shift layout slightly.
      this.cards.forEach((card) => {
        const media = card.querySelector('video, img');
        if (!media) return;
        media.addEventListener('loadedmetadata', measure, { once: true });
        media.addEventListener('load', measure, { once: true });
      });
    }

    /* ---------- Home grid: arrow scroll ---------- */

    bindTrackControls() {
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => {
          this.track.scrollBy({ left: -this.track.clientWidth * 0.7, behavior: 'smooth' });
        });
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => {
          this.track.scrollBy({ left: this.track.clientWidth * 0.7, behavior: 'smooth' });
        });
      }
    }

    bindCardClicks() {
      this.cards.forEach((card) => {
        card.addEventListener('click', () => {
          if (!this.popupEnabled || !this.modal) {
            const url = card.dataset.productUrl;
            if (url) window.location.href = url;
            return;
          }
          const index = parseInt(card.dataset.reelOpen, 10) || 0;
          this.openModal(index);
        });
      });
    }

    /* ---------- Add to cart: shared by home-grid mini forms and the
       popup's full product card form ---------- */

    bindAddToCartForms() {
      const forms = Array.from(this.root.querySelectorAll('[data-reel-atc-form]'));
      forms.forEach((form) => {
        form.addEventListener('submit', (event) => this.handleAddToCart(event, form));
      });
    }

    /* ---------- Modal ---------- */

    bindModalControls() {
      this.closeEls.forEach((el) => el.addEventListener('click', () => this.closeModal()));

      if (this.modalPrevBtn) {
        this.modalPrevBtn.addEventListener('click', () => this.goTo(this.activeIndex - 1));
      }
      if (this.modalNextBtn) {
        this.modalNextBtn.addEventListener('click', () => this.goTo(this.activeIndex + 1));
      }
      if (this.soundBtn) {
        this.soundBtn.addEventListener('click', () => this.toggleSound());
      }
      if (this.likeBtn) {
        this.likeBtn.addEventListener('click', () => this.toggleLike());
      }
      if (this.shareBtn) {
        this.shareBtn.addEventListener('click', () => this.shareActive());
      }

      document.addEventListener('keydown', (event) => {
        if (!this.modal.classList.contains('is-open')) return;
        if (event.key === 'Escape') this.closeModal();
        if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') this.goTo(this.activeIndex - 1);
        if (event.key === 'ArrowDown' || event.key === 'ArrowRight') this.goTo(this.activeIndex + 1);
      });

      this.stage.addEventListener(
        'wheel',
        (event) => {
          if (!this.modal.classList.contains('is-open') || this.wheelLocked) return;
          event.preventDefault();
          this.wheelLocked = true;
          if (event.deltaY > 0) this.goTo(this.activeIndex + 1);
          else if (event.deltaY < 0) this.goTo(this.activeIndex - 1);
          setTimeout(() => (this.wheelLocked = false), 450);
        },
        { passive: false }
      );

      this.stage.addEventListener(
        'touchstart',
        (event) => {
          this.touchStartY = event.touches[0].clientY;
        },
        { passive: true }
      );

      this.stage.addEventListener(
        'touchend',
        (event) => {
          if (this.touchStartY === null) return;
          const deltaY = this.touchStartY - event.changedTouches[0].clientY;
          if (Math.abs(deltaY) > 60) {
            if (deltaY > 0) this.goTo(this.activeIndex + 1);
            else this.goTo(this.activeIndex - 1);
          }
          this.touchStartY = null;
        },
        { passive: true }
      );

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          this.goTo(parseInt(dot.dataset.reelDot, 10));
        });
      });
    }

    /* ---------- Requirement 6: desktop mouse-drag swipe ---------- */

    bindDragSwipe() {
      const isInteractive = (target) =>
        target.closest('button, a, form, input, [data-reels-modal-prev], [data-reels-modal-next]');

      this.stage.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse') return; // touch is handled separately above
        if (isInteractive(event.target)) return;
        this.isDragging = true;
        this.dragStartX = event.clientX;
        this.dragDeltaX = 0;
        this.stage.classList.add('is-dragging');
        this.stage.setPointerCapture(event.pointerId);
      });

      this.stage.addEventListener('pointermove', (event) => {
        if (!this.isDragging) return;
        this.dragDeltaX = event.clientX - this.dragStartX;
      });

      const endDrag = (event) => {
        if (!this.isDragging) return;
        this.isDragging = false;
        this.stage.classList.remove('is-dragging');
        if (Math.abs(this.dragDeltaX) > DRAG_THRESHOLD) {
          if (this.dragDeltaX < 0) this.goTo(this.activeIndex + 1);
          else this.goTo(this.activeIndex - 1);
        }
        this.dragDeltaX = 0;
      };

      this.stage.addEventListener('pointerup', endDrag);
      this.stage.addEventListener('pointercancel', endDrag);
      this.stage.addEventListener('pointerleave', (event) => {
        if (this.isDragging) endDrag(event);
      });
    }

    openModal(index) {
      this.modal.classList.add('is-open');
      this.modal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('stanlee-reels-lock-scroll');
      this.goTo(index, true);
    }

    closeModal() {
      // Requirement 12: closing the popup never touches the home track's
      // scroll position — we simply don't call any scroll method on it.
      this.modal.classList.remove('is-open');
      this.modal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('stanlee-reels-lock-scroll');
      this.slides.forEach((slide) => {
        const video = slide.querySelector('[data-reel-modal-video]');
        if (video) video.pause();
      });
      this.stopProgressTracking();
    }

    goTo(index, isInitial) {
      if (!this.slides.length) return;
      let nextIndex = index;
      if (this.loop) {
        nextIndex = (index + this.slides.length) % this.slides.length;
      } else {
        nextIndex = Math.max(0, Math.min(index, this.slides.length - 1));
      }
      if (!isInitial && nextIndex === this.activeIndex) return;

      this.activeIndex = nextIndex;

      this.slides.forEach((slide, i) => {
        slide.classList.remove('is-active', 'is-prev', 'is-next');
        if (i === this.activeIndex) slide.classList.add('is-active');
        else if (i === this.activeIndex - 1) slide.classList.add('is-prev');
        else if (i === this.activeIndex + 1) slide.classList.add('is-next');

        const video = slide.querySelector('[data-reel-modal-video]');
        if (!video) return;

        if (i === this.activeIndex) {
          this.loadModalVideo(video);
          video.muted = this.isMuted;
          video.currentTime = video.currentTime || 0;
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });

      // Preload neighbouring videos only.
      [this.activeIndex - 1, this.activeIndex + 1].forEach((i) => {
        const slide = this.slides[i];
        if (!slide) return;
        const video = slide.querySelector('[data-reel-modal-video]');
        if (video) this.loadModalVideo(video);
      });

      this.dots.forEach((dot) => {
        dot.classList.toggle('is-active', parseInt(dot.dataset.reelDot, 10) === this.activeIndex);
      });

      this.updateProgressBar();
    }

    loadModalVideo(video) {
      if (video.dataset.loaded) return;
      const src = video.dataset.src;
      if (!src) return;
      video.src = src;
      video.loop = this.loop;
      video.dataset.loaded = 'true';
      video.load();
    }

    toggleSound() {
      this.isMuted = !this.isMuted;
      this.soundBtn.setAttribute('aria-pressed', String(!this.isMuted));
      const activeSlide = this.slides[this.activeIndex];
      if (activeSlide) {
        const video = activeSlide.querySelector('[data-reel-modal-video]');
        if (video) video.muted = this.isMuted;
      }
    }

    /* ---------- Requirement 10: progress bar, like, share ---------- */

    updateProgressBar() {
      if (!this.progressSegs.length) return;
      this.stopProgressTracking();

      this.progressSegs.forEach((seg) => {
        const i = parseInt(seg.dataset.progressSeg, 10);
        seg.classList.toggle('is-complete', i < this.activeIndex);
        seg.classList.toggle('is-active', i === this.activeIndex);
        const fill = seg.querySelector('[data-progress-fill]');
        if (fill && i !== this.activeIndex && i > this.activeIndex) fill.style.width = '0%';
      });

      const activeSlide = this.slides[this.activeIndex];
      const activeSeg = this.progressSegs[this.activeIndex];
      if (!activeSlide || !activeSeg) return;
      const video = activeSlide.querySelector('[data-reel-modal-video]');
      const fill = activeSeg.querySelector('[data-progress-fill]');
      if (!video || !fill) return;

      fill.style.width = '0%';

      this._progressHandler = () => {
        if (!video.duration) return;
        const pct = Math.min(100, (video.currentTime / video.duration) * 100);
        fill.style.width = pct + '%';
      };
      video.addEventListener('timeupdate', this._progressHandler);
      this._progressVideo = video;
    }

    stopProgressTracking() {
      if (this._progressVideo && this._progressHandler) {
        this._progressVideo.removeEventListener('timeupdate', this._progressHandler);
      }
      this._progressVideo = null;
      this._progressHandler = null;
    }

    toggleLike() {
      const liked = this.likeBtn.classList.toggle('is-liked');
      this.likeBtn.setAttribute('aria-pressed', String(liked));
    }

    shareActive() {
      const activeSlide = this.slides[this.activeIndex];
      const link = activeSlide ? activeSlide.querySelector('[data-reel-product-card] a') : null;
      const url = link ? link.href : window.location.href;

      if (navigator.share) {
        navigator.share({ url }).catch(() => {});
        return;
      }
      if (navigator.clipboard) {
        navigator.clipboard.writeText(url).catch(() => {});
      }
    }

    /* ---------- AJAX add to cart (home grid + popup) ---------- */

    handleAddToCart(event, form) {
      event.preventDefault();
      const button = form.querySelector('[data-reel-atc-btn]');
      if (!button || button.disabled) return;

      button.classList.remove('is-added');
      button.classList.add('is-loading');
      button.disabled = true;

      const formData = new FormData(form);

      fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/json' },
      })
        .then((response) => {
          if (!response.ok) throw new Error('Add to cart failed');
          return response.json();
        })
        .then(() => {
          button.classList.remove('is-loading');
          button.classList.add('is-added');
          this.refreshCartState();
          setTimeout(() => {
            button.classList.remove('is-added');
            button.disabled = false;
          }, 1800);
        })
        .catch(() => {
          button.classList.remove('is-loading');
          button.disabled = false;
        });
    }

    refreshCartState() {
      // Update cart count bubble(s) and mini cart if present, without a page reload.
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((response) => response.json())
        .then((cart) => {
          document.dispatchEvent(new CustomEvent('stanlee-reels:cart-updated', { detail: cart }));

          const countEls = document.querySelectorAll('[data-cart-count], .cart-count-bubble');
          countEls.forEach((el) => {
            el.textContent = cart.item_count;
          });

          // If the theme exposes a cart drawer/mini-cart section renderer, ask it to refresh.
          if (typeof window.Shopify !== 'undefined' && document.querySelector('cart-drawer')) {
            document.querySelector('cart-drawer').dispatchEvent(new CustomEvent('cart:refresh'));
          }
        })
        .catch(() => {});
    }
  }

  function initStanleeReels() {
    document.querySelectorAll('.stanlee-reels').forEach((root) => {
      if (root.dataset.stanleeReelsInit) return;
      root.dataset.stanleeReelsInit = 'true';
      new StanleeReels(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStanleeReels);
  } else {
    initStanleeReels();
  }

  // Re-init when a section is added/re-rendered in the Theme Editor.
  document.addEventListener('shopify:section:load', initStanleeReels);
})();
